import { Group, Text } from "@mantine/core"
import { ColumnDef, createColumnHelper } from "@tanstack/react-table"
import { ActionIcon } from "@tfg/ui"
import { useMemo } from "react"

import { AdminResponses } from "@tfg/types/endpoints/admin"

import { EditableCellWithTextInput } from "~/pages/Admin/Translations/columns"

import { MultiSelectCell } from "./MultiSelectCell"
import { useDeleteStandardMutation, useUpdateStandardMutation } from "./hooks"

export function useColumns(
  certificatesTypesList: AdminResponses.CertificateTypeList[]
) {
  const columnHelper = createColumnHelper<AdminResponses.Standards>()
  const { mutate: deleteStandard } = useDeleteStandardMutation()
  const { mutate: updateStandard, isLoading } = useUpdateStandardMutation()

  return useMemo<ColumnDef<AdminResponses.Standards, any>[]>(
    () => [
      columnHelper.accessor("id", {
        header: "ID",
        cell: ({ getValue }) => <Text>{getValue()}</Text>,
      }),
      columnHelper.accessor("name", {
        header: "Name",
        cell: ({ getValue, row }) => (
          <EditableCellWithTextInput
            defaultValue={getValue()}
            onSave={(newValue) =>
              updateStandard({
                id: row.original.id,
                name: newValue,
              })
            }
          />
        ),
      }),
      columnHelper.accessor("certificateTypes", {
        header: "Certificates",
        enableSorting: false,
        cell: ({ row }) => {
          const list = certificatesTypesList.map((certificateType) => ({
            value: certificateType.id.toString(),
            label: certificateType.key,
          }))
          const certificateTypes = row.original.certificateTypes
          const _values = certificateTypes.map((certificateType) =>
            certificateType.id.toString()
          )
          return (
            <MultiSelectCell
              data={list}
              initialValues={_values}
              isLoading={isLoading}
              key={row.original.id}
              onChange={(value) => {
                updateStandard({
                  ...row.original,
                  certificateTypes: value,
                })
              }}
            />
          )
        },
      }),
      columnHelper.accessor((row) => row.id, {
        header: () => null,
        id: "actions",
        enableSorting: false,
        cell: ({ row }) => (
          <Group wrap="nowrap" gap="xs">
            <ActionIcon
              type="delete"
              onClick={() => deleteStandard(row.original.id)}
            />
          </Group>
        ),
      }),
    ],
    [
      columnHelper,
      deleteStandard,
      updateStandard,
      certificatesTypesList,
      isLoading,
    ]
  )
}
